import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import {
  RequestServiceUpdateAction,
  SetServiceAction,
  RequestVideoAddAction,
  VideoAddAction,
  RequestVideoDeleteAction,
  VideoDeleteAction,
  RequestVideosReorderAction,
  VideosReorderAction,
  MediaActionConstructor,
} from '../store/service/reducer';
import { environment } from '../../environments/environment';
import { Service } from '../store/service/Service';
import { AuthHttpService } from '../services/auth-http.service';
import { map, mergeMap, switchMap } from 'rxjs/operators';
import { RequestErrorAction } from '../store/serviceUi/reducer';
import { requestErrorMap, requestFieldMap } from './pipes';
import {
  REQUEST_SERVICE_UPDATE,
  REQUEST_VIDEO_ADD,
  REQUEST_VIDEO_DELETE,
  REQUEST_VIDEO_REORDER,
} from '../store/service/constants';
import { Video } from '../store/service/Video';
import { Observable } from 'rxjs/internal/Observable';
import { LOCALE_PREFIX } from '../configs';

const responseFieldMap = (errorMessage: string) => {
  return (res: any) => {
    if (res.hasError) {
      return new RequestErrorAction({ errorMessage, field: res.field });
    }
    return new SetServiceAction({ res: res.res.res, field: res.res.field });
  };
};

const responseMediaMap = (
  errorMessage: string,
  field: string,
  ResAction: MediaActionConstructor,
) => {
  return (res: any) => {
    if (res.hasError) {
      return new RequestErrorAction({ errorMessage, field });
    }
    return new ResAction(res.res);
  };
};

@Injectable()
export class ServiceEffects {
  private baseUrl = `${environment.urlPrefix}${LOCALE_PREFIX}/update_api/api/service`;

  @Effect()
  updateField$: Observable<Action> = this.actions$.pipe(
    ofType(REQUEST_SERVICE_UPDATE),
    mergeMap((action: RequestServiceUpdateAction) => {
      return this.authHttp
        .patch<Service>(`${this.baseUrl}/${action.payload.id}/`, {
          [action.payload.key]: action.payload.value,
        })
        .pipe(
          requestFieldMap(action.payload.key),
          requestErrorMap(action.payload.key),
        );
    }),
    map(responseFieldMap('Error during service update.')),
  );

  @Effect()
  addVideo$: Observable<Action> = this.actions$.pipe(
    ofType(REQUEST_VIDEO_ADD),
    mergeMap((action: RequestVideoAddAction) => {
      const { service, video } = action.payload;
      return this.authHttp
        .post<Video>(`${this.baseUrl}/${service.id}/video/`, video)
        .pipe(requestErrorMap());
    }),
    map(responseMediaMap('Could not add video.', 'videos', VideoAddAction)),
  );

  @Effect()
  deleteVideo$: Observable<Action> = this.actions$.pipe(
    ofType(REQUEST_VIDEO_DELETE),
    mergeMap((action: RequestVideoDeleteAction) => {
      const { service, video } = action.payload;
      const url = `${this.baseUrl}/${service.id}/video/${video.id}/`;
      return this.authHttp.delete(url).pipe(
        map(() => video.id),
        requestErrorMap(),
      );
    }),
    map(
      responseMediaMap('Could not delete video.', 'videos', VideoDeleteAction),
    ),
  );

  @Effect()
  reorderVideos$: Observable<Action> = this.actions$.pipe(
    ofType(REQUEST_VIDEO_REORDER),
    switchMap((action: RequestVideosReorderAction) => {
      const { service, ids } = action.payload;
      return this.authHttp
        .patch(`${this.baseUrl}/${service.id}/reorder_videos/`, ids)
        .pipe(requestErrorMap());
    }),
    map(
      responseMediaMap(
        'Could not reorder videos.',
        'videos',
        VideosReorderAction,
      ),
    ),
  );

  constructor(private actions$: Actions, private authHttp: AuthHttpService) {}
}
